import { ref, watch } from 'vue';
import { todayIn, calendarToJSDate, addDays } from '@/utils/dt';
import { useTimezoneStore } from '@/stores/timezone';
import type { DateRangeValue } from '@/utils/datePickerPresets';

/**
 * Provides a `selectedDateRange` ref pre-set to the last 30 days ending today
 * (`[today-29, today]`) in the user's effective timezone.
 *
 * The ref starts as `null` and receives its first value only after the
 * timezone store has settled (profile loaded or failed). This guarantees
 * all downstream watchers fire exactly once with the correct timezone —
 * no race condition, no double-fetch.
 */
export function useDateRangeWithTimezone() {
    const tzStore = useTimezoneStore();

    function buildRange(tz: string): [Date, Date] {
        const today = todayIn(tz);
        return [
            calendarToJSDate(addDays(today, -29)),
            calendarToJSDate(today),
        ];
    }

    const selectedDateRange = ref<DateRangeValue>(null);

    // Wait for the timezone to settle, then emit the first value.
    tzStore.whenReady.then(() => {
        selectedDateRange.value = buildRange(tzStore.effectiveTimezone);
    });

    // If the user changes timezone later (settings page), rebuild.
    watch(() => tzStore.effectiveTimezone, (tz) => {
        if (selectedDateRange.value !== null) {
            selectedDateRange.value = buildRange(tz);
        }
    });

    // If the user clears the picker, reset to the default range.
    watch(selectedDateRange, (val) => {
        if (val === null) {
            selectedDateRange.value = buildRange(tzStore.effectiveTimezone);
        }
    });

    return { selectedDateRange };
}
